import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Github, Mail, Linkedin, ExternalLink, ArrowRight,
  Layers, Zap, Database, Terminal, Brain,
} from "lucide-react";
import "./App.css";

// —————————————————————————————————————————————————————————————
// HOOKS
// —————————————————————————————————————————————————————————————

function useTypewriter(phrases, speed = 80, pause = 2400) {
  const [display, setDisplay] = useState("");
  const [phase, setPhase] = useState("typing");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const current = phrases[idx];
    let timer;
    if (phase === "typing") {
      if (display.length < current.length) {
        timer = setTimeout(() => setDisplay(current.slice(0, display.length + 1)), speed);
      } else {
        timer = setTimeout(() => setPhase("deleting"), pause);
      }
    } else {
      if (display.length > 0) {
        timer = setTimeout(() => setDisplay(display.slice(0, -1)), speed / 2);
      } else {
        setIdx((i) => (i + 1) % phrases.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(timer);
  }, [display, phase, idx, phrases, speed, pause]);

  return display;
}

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("revealed"); }),
      { threshold: 0.08, rootMargin: "0px 0px -44px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
}

function useScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const handler = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setPct((scrollTop / (scrollHeight - clientHeight)) * 100);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return pct;
}

// —————————————————————————————————————————————————————————————
// DATA
// —————————————————————————————————————————————————————————————

const PROJECTS = [
  {
    id: "documind",
    featured: true,
    title: "DocuMind AI",
    subtitle: "RAG · IA Locale · Chat PDF",
    description:
      "Application RAG haute performance pour discuter en temps réel avec vos documents PDF via une IA entièrement locale (Ollama / Qwen 2.5). Architecture monorepo : streaming de tokens, vector database ChromaDB, architecture FastAPI + React TypeScript.",
    tags: ["React", "TypeScript", "FastAPI", "Python", "LangChain", "ChromaDB", "Ollama"],
    github: "https://github.com/achrafH99/documindAI",
    year: "2026",
    color: "cyan",
  },
  {
    id: "basenautique",
    title: "Base Nautique",
    subtitle: "Gestion · TypeScript · Full-Stack",
    description:
      "Application complète de gestion pour une base nautique : réservations, suivi de flotte et gestion clients. Stack TypeScript moderne, architecture orientée domaine.",
    tags: ["TypeScript", "React", "Node.js"],
    github: "https://github.com/achrafH99/base-nautique",
    year: "2025",
    color: "teal",
  },
  {
    id: "ecommerce",
    title: "eCommerce Angular",
    subtitle: "Angular · TypeScript · Commerce",
    description:
      "Plateforme e-commerce Angular complète : catalogue produits, panier, gestion commandes. Architecture orientée composants, RxJS state management.",
    tags: ["TypeScript", "Angular", "RxJS"],
    github: "https://github.com/achrafH99/eCommerce",
    year: "2025",
    color: "orange",
  },
  {
    id: "reportcountries",
    title: "Report Countries",
    subtitle: "TypeScript · Data Viz · Reporting",
    description:
      "Application de reporting et visualisation de données pays avec filtres et indicateurs clés. Focus sur clarté des dashboards et restitution data orientée décision.",
    tags: ["TypeScript", "Reporting", "Data Visualization"],
    github: "https://github.com/achrafH99/report-countries",
    year: "2025",
    color: "amber",
  },
  {
    id: "ecommercesymfony",
    title: "eCommerce Symfony",
    subtitle: "PHP · Symfony · Full-Stack",
    description:
      "Application e-commerce full-stack avec Symfony : authentification, catalogue, panier et backoffice. Structure MVC et logique métier côté serveur.",
    tags: ["PHP", "Symfony", "MySQL", "Twig"],
    github: "https://github.com/achrafH99/eCommerceSymfony",
    year: "2023",
    color: "rose",
  },
];

const SKILLS = [
  {
    category: "Frontend",
    icon: Layers,
    items: ["React / Next.js", "Angular", "TypeScript", "Tailwind CSS", "Vue.js"],
  },
  {
    category: "Backend",
    icon: Terminal,
    items: ["Node.js (NestJS / Express)", "Python (FastAPI / Django)", "Java (Spring Boot)", "PHP (Symfony / Laravel)"],
  },
  {
    category: "Bases de données",
    icon: Database,
    items: ["PostgreSQL", "MongoDB", "MySQL", "MariaDB", "Oracle", "ChromaDB"],
  },
  {
    category: "DevOps & Outils",
    icon: Zap,
    items: ["Docker", "GitHub Actions", "CI/CD", "Azure", "Git", "Agile / Scrum"],
  },
];

const EXPERIENCES = [
  {
    company: "Afnet",
    role: "Développeur Full Stack — CDI",
    period: "Jan 2023 → Présent",
    tags: ["Java Spring Boot", "React", "Next.js", "Node.js"],
    description:
      "Développement d'applications web industrielles, création et suivi de processus automatisés, visualisation et traitement de données complexes.",
    current: true,
  },
  {
    company: "Soprasteria",
    role: "Développeur Full Stack — CDI",
    period: "Sep 2022 → Jan 2023",
    tags: ["Java", "PostgreSQL"],
    description:
      "Requêtes SQL complexes (PostgreSQL), documentation et analyse de cahier des charges.",
    current: false,
  },
  {
    company: "Opteamis",
    role: "Développeur Full Stack — Alternance",
    period: "Sep 2021 → Sep 2022",
    tags: ["PHP MVC", "jQuery", "Bootstrap"],
    description:
      "Système de matching sur plateforme existante, amélioration UX/UI et optimisation des performances front & back.",
    current: false,
  },
  {
    company: "Atingo Consulting",
    role: "Développeur — Alternance",
    period: "Oct 2020 → Sep 2021",
    tags: ["PowerShell", "Node.js", "Power Automate", "Azure"],
    description:
      "Scripts de migration de réseau social d'entreprise, automatisation de processus avec Power Automate et Azure.",
    current: false,
  },
  {
    company: "Adileos",
    role: "Développeur Full Stack — Alternance",
    period: "Sep 2018 → Sep 2020",
    tags: ["Angular", "Node.js", "NestJS", "PHP"],
    description:
      "Webapp mobile Adileos, automatisation import Excel → base de données, gestion de projet en équipe.",
    current: false,
  },
];

const EDUCATION = [
  {
    school: "CFA INSTA · Paris",
    degree: "Master Architecte Informatique",
    period: "2020 → 2022",
    items: ["Algorithme Avancé", "Ingénierie Logicielle", "Gestion de projet"],
  },
  {
    school: "La Sorbonne UPMC · Paris",
    degree: "Licence Pro Projet Web & Mobile",
    period: "2019 → 2020",
    items: ["Design Web", "Programmation objet & frameworks", "Gestion de projet"],
  },
  {
    school: "Lycée Turgot · Paris",
    degree: "BTS SIO SLAM",
    period: "2017 → 2019",
    items: ["Algorithmique appliquée", "Java, PHP, SQL"],
  },
];

// —————————————————————————————————————————————————————————————
// COMPONENTS
// —————————————————————————————————————————————————————————————

function TiltCard({ children, className = "", style, ...props }) {
  const ref = useRef(null);

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 9;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -9;
    el.style.transition = "transform 0.12s ease, border-color 0.3s, box-shadow 0.3s";
    el.style.transform = `perspective(960px) rotateX(${y}deg) rotateY(${x}deg) translateZ(8px)`;
  }, []);

  const onLeave = useCallback(() => {
    if (!ref.current) return;
    ref.current.style.transition = "transform 0.45s ease, border-color 0.3s, box-shadow 0.3s";
    ref.current.style.transform = "perspective(960px) rotateX(0) rotateY(0) translateZ(0)";
  }, []);

  return (
    <div
      ref={ref}
      className={`glass-card ${className}`}
      style={style}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...props}
    >
      {children}
    </div>
  );
}

// —————————————————————————————————————————————————————————————
// APP
// —————————————————————————————————————————————————————————————

export default function App() {
  const typeText = useTypewriter([
    "Développeur Full-Stack",
    "Ingénieur Logiciel",
    "Passionné de code et d'innovation",
    "Intérêt pour l'IA et les nouvelles technologies",
  ]);
  const scrollPct = useScrollProgress();
  useScrollReveal();

  return (
    <>
      {/* —— Scroll progress bar —— */}
      <div className="scroll-progress" style={{ width: `${scrollPct}%` }} />

      {/* —— Animated background —— */}
      <div className="bg-scene" aria-hidden="true">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="grid-overlay" />
      </div>

      <div className="main-wrap">

        {/* """""""""""""""""""" NAV """""""""""""""""""" */}
        <nav>
          <div className="container nav-inner">
            <span className="nav-logo">AH.</span>
            <ul className="nav-links">
              {[["#hero", "Accueil"], ["#projects", "Projets"], ["#skills", "Stack"], ["#parcours", "Parcours"], ["#contact", "Contact"]].map(
                ([href, label]) => (
                  <li key={label}><a href={href}>{label}</a></li>
                )
              )}
            </ul>
            <a href="#contact" className="nav-cta">Me contacter</a>
          </div>
        </nav>

        {/* """""""""""""""""""" HERO """""""""""""""""""" */}
        <section id="hero">
          <div className="container">

            <div className="hero-badge" data-reveal>
              <span className="pulse-dot" />
              Disponible pour CDI · île-de-France
            </div>

            <h1 className="hero-title" data-reveal data-reveal-delay="1">
              Achraf
              <br />
              <span className="gradient">Hanini</span>
            </h1>

            <div className="hero-typewriter" data-reveal data-reveal-delay="2">
              {typeText}
              <span className="tw-cursor" />
            </div>

            <p className="hero-desc" data-reveal data-reveal-delay="3">
              Full-Stack Engineer avec 5+ ans d'expérience en CDI et alternances. 
              <br></br>Curieux, passionné, avec l'envie d'apprendre et de créer des applications à fort impact. 
              <br></br>Mes expériences diversifiées, du développement d'applications industrielles à la création de projets personnels innovants, témoignent de ma capacité à m'adapter et à apprendre rapidement de nouvelles technologies. 
              <br></br>Ouvert à des opportunités CDI en île-de-France et en full remote. 
              <br></br>N'hésitez pas à me contacter pour discuter d'un projet ou d'un poste.
            </p>

            <div className="hero-actions" data-reveal data-reveal-delay="4">
              <a href="#projects" className="btn-primary">
                Voir mes projets <ArrowRight size={16} />
              </a>
              <a
                href="https://github.com/achrafH99"
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
              >
                <Github size={16} /> GitHub
              </a>
            </div>


            <div className="stats-strip" data-reveal>
              {[
                { num: "5+",        label: "Années d'expérience" },
                { num: "2 CDI",     label: "Expériences longues durée" },
                { num: "Full-Stack",label: "Front · Back · Mobile · IA" },
              ].map((s) => (
                <div key={s.num} className="stat-item">
                  <span className="stat-num">{s.num}</span>
                  <span className="stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* """""""""""""""""""" PROJECTS """""""""""""""""""" */}
        <section id="projects">
          <div className="container">

            <div className="projects-header" data-reveal>
              <div className="section-label">
                <span />Réalisations<span />
              </div>
              <h2 className="section-title">Projets sélectionnés</h2>
              <p className="section-sub">
                Des projets concrets — chaque ligne de code pensée pour l'impact.
              </p>
            </div>

            {/* Featured */}
            <TiltCard
              className="project-featured"
              data-reveal
              data-reveal-delay="1"
            >
              <div className="project-featured-content">
                <span className="project-badge badge-featured">⭐ Projet phare</span>
                <h3 className="project-title">{PROJECTS[0].title}</h3>
                <p className="project-subtitle">{PROJECTS[0].subtitle}</p>
                <p className="project-desc">{PROJECTS[0].description}</p>
                <div className="tags">
                  {PROJECTS[0].tags.map((t) => (
                    <span key={t} className="tag">{t}</span>
                  ))}
                </div>
                <a
                  href={PROJECTS[0].github}
                  target="_blank"
                  rel="noreferrer"
                  className="project-link"
                >
                  <Github size={14} /> Voir sur GitHub <ExternalLink size={12} />
                </a>
              </div>
              <div className="project-featured-visual">
                <span className="pf-icon-bg">AI</span>
                <Brain
                  size={108}
                  strokeWidth={0.6}
                  style={{ color: "rgba(0,212,255,0.18)", position: "relative", zIndex: 1 }}
                />
              </div>
            </TiltCard>

            {/* Grid */}
            <div className="projects-grid">
              {PROJECTS.slice(1).map((p, i) => (
                <TiltCard
                  key={p.id}
                  className="project-card"
                  data-reveal
                  data-reveal-delay={String(i + 1)}
                >
                  <div className="project-card-header">
                    <div
                      className="color-dot"
                      style={{ background: `var(--c-${p.color})` }}
                    />
                    <span className="project-year">{p.year}</span>
                  </div>
                  <div>
                    <h3 className="project-card-title">{p.title}</h3>
                    <p className="project-card-sub">{p.subtitle}</p>
                  </div>
                  <p className="project-card-desc">{p.description}</p>
                  <div className="tags">
                    {p.tags.map((t) => (
                      <span key={t} className="tag">{t}</span>
                    ))}
                  </div>
                  <a
                    href={p.github}
                    target="_blank"
                    rel="noreferrer"
                    className="project-link"
                  >
                    <Github size={13} /> GitHub <ExternalLink size={11} />
                  </a>
                </TiltCard>
              ))}
            </div>
          </div>
        </section>

        {/* """""""""""""""""""" SKILLS """""""""""""""""""" */}
        <section id="skills">
          <div className="container">
            <div data-reveal>
              <div className="section-label"><span />Compétences</div>
              <h2 className="section-title">Stack technique</h2>
              <p className="section-sub">
                Technologies maîtrisées en production, du prototype au déploiement.
              </p>
            </div>
            <div className="skills-grid">
              {SKILLS.map((s, i) => (
                <TiltCard
                  key={s.category}
                  className="skill-card"
                  data-reveal
                  data-reveal-delay={String(i + 1)}
                >
                  <div className="skill-icon">
                    <s.icon size={22} />
                  </div>
                  <div className="skill-category">{s.category}</div>
                  <div className="skill-items">
                    {s.items.map((it) => (
                      <span key={it} className="skill-tag">{it}</span>
                    ))}
                  </div>
                </TiltCard>
              ))}
            </div>
          </div>
        </section>

        {/* """""""""""""""""""" PARCOURS """""""""""""""""""" */}
        <section id="parcours">
          <div className="container">
            <div data-reveal>
              <div className="section-label"><span />Parcours</div>
              <h2 className="section-title">Expériences &amp; Formation</h2>
            </div>

            <div className="parcours-grid">
              {/* —— Experiences —— */}
              <div data-reveal data-reveal-delay="1">
                <div className="timeline-col-title">Expériences professionnelles</div>
                <div className="timeline-list">
                  {EXPERIENCES.map((exp) => (
                    <div
                      key={exp.company}
                      className={`timeline-item${exp.current ? " current" : ""}`}
                    >
                      <div className="timeline-period">{exp.period}</div>
                      <div className="timeline-company">{exp.company}</div>
                      <div className="timeline-role">{exp.role}</div>
                      <div className="timeline-desc">{exp.description}</div>
                      <div className="timeline-tags">
                        {exp.tags.map((t) => (
                          <span key={t} className="timeline-tag">{t}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* —— Education —— */}
              <div data-reveal data-reveal-delay="2">
                <div className="timeline-col-title">Formation</div>
                {EDUCATION.map((edu) => (
                  <div key={edu.school} className="glass-card edu-item">
                    <div className="edu-period">{edu.period}</div>
                    <div className="edu-school">{edu.school}</div>
                    <div className="edu-degree">{edu.degree}</div>
                    <ul className="edu-items">
                      {edu.items.map((it) => (
                        <li key={it}>{it}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* """""""""""""""""""" CONTACT """""""""""""""""""" */}
        <section id="contact">
          <div className="container">
            <div className="contact-wrap">

              {/* Info */}
              <div className="contact-info" data-reveal>
                <div className="section-label"><span />Contact</div>
                <h2>
                  Travaillons<br />
                  <span className="gradient">ensemble.</span>
                </h2>
                <p>
                  Ouvert à des opportunités CDI en île-de-France et en full remote.
                  N'hésitez pas à me contacter pour discuter d'un projet ou d'un poste.
                </p>
                <div className="contact-links">
                  <a
                    href="https://github.com/achrafH99"
                    target="_blank"
                    rel="noreferrer"
                    className="contact-link"
                  >
                    <Github size={17} className="contact-link-icon" />
                    github.com/achrafH99
                  </a>
                </div>
              </div>

              {/* Form */}
              <div data-reveal data-reveal-delay="2">
                <form
                  action="https://formspree.io/f/your-id"
                  method="POST"
                  className="contact-form"
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="cf-name">Nom</label>
                      <input
                        id="cf-name"
                        name="name"
                        type="text"
                        placeholder="Votre nom"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="cf-email">Email</label>
                      <input
                        id="cf-email"
                        name="email"
                        type="email"
                        placeholder="votre@email.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cf-subject">Sujet</label>
                    <input
                      id="cf-subject"
                      name="subject"
                      type="text"
                      placeholder="CDI Full-Stack — [Votre entreprise]"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cf-message">Message</label>
                    <textarea
                      id="cf-message"
                      name="message"
                      rows={5}
                      placeholder="Décrivez votre projet ou l'opportunité..."
                      required
                    />
                  </div>
                  <button type="submit" className="btn-submit">
                    Envoyer le message <ArrowRight size={16} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* """""""""""""""""""" FOOTER """""""""""""""""""" */}
        <footer>
          <div className="container footer-inner">
            <span className="footer-logo">Achraf Hanini</span>
            <span className="footer-copy">
              © {new Date().getFullYear()} · Développeur Full-Stack · Fait avec passion
            </span>
            <div className="footer-socials">
              <a href="mailto:achrafhanini@gmail.com" className="social-link" title="Email">
                <Mail size={16} />
              </a>
              <a
                href="https://github.com/achrafH99"
                target="_blank"
                rel="noreferrer"
                className="social-link"
                title="GitHub"
              >
                <Github size={16} />
              </a>
              <a
                href="https://www.linkedin.com/in/"
                target="_blank"
                rel="noreferrer"
                className="social-link"
                title="LinkedIn"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
